import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';

import { actionCreatorsApp } from '@app/state';
import { CONFIRMATION_MODAL_INITIAL_STATE } from '@app/state/app/reducers';
import { IConfirmationModal } from '@app/state/app/types';
import React, { useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import Modal from '../../Modal';
import Typography from '../../Typography';
import { btnBaseStyles } from '../styles';
import Icon from '../../Icon';
import { useTheme } from '@emotion/react';
import { displayFlexRowStyles } from '@app/theme/commonStyles';
import List from '../../List';
import ListItem from '../../List/ListItem';
import Divider from '../../Divider';
import { deleteListItemStyles } from '../../List/ListItem/styles';

interface IDeleteMultipleConfirmationModal extends IConfirmationModal, WithTranslation {
  componentDescription?: React.ReactNode;
  inactive?: boolean;
}

const DeleteMultipleConfirmationModal = ({
  title,
  description,
  active,
  onCancel,
  onSuccess,
  btnCommonCsx: btnCommonCsx,
  btnCancelText,
  btnCancelCsx: btnCancelCsx,
  btnSuccessText,
  btnSuccessCsx: btnSuccessCsx,
  runCancelOnClickOutside,
  size,
  t,
  hideSuccess,
  hideCancel,
  overrideTitle,
  autoclose = true,
  autoCloseOnCancel = true,
  data,
  inactive,
}: IDeleteMultipleConfirmationModal) => {
  const { setShowConfirmationModal } = bindActionCreators(
    actionCreatorsApp,
    useDispatch(),
  );

  const theme = useTheme();

  const handleOnModalClose = () => {
    if (runCancelOnClickOutside && onCancel) {
      onCancel();
    }
    autoCloseOnCancel &&
      setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
  };

  const handleOnCancel = () => {
    if (onCancel) onCancel();
    autoCloseOnCancel &&
      setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
  };

  const handleOnModalSuccess = () => {
    if (onSuccess) onSuccess();
    autoclose && setShowConfirmationModal(CONFIRMATION_MODAL_INITIAL_STATE);
  };

  const ids = useMemo(
    () => Object.keys(data || {}),
    [data]
  );

  return (
    <Modal
      isActive={active}
      size={size || 500}
      minHeight={375}
      animationMode="alert"
      title={
        <Box csx={[displayFlexRowStyles, { gap: '10px' }]}>
          <Icon name="IoMdWarning" color={theme.colors.semanticRed} size='27px' />
          <Box>
            <Typography variant="heading" fontWeight="medium">
              {overrideTitle
                ? title
                : t(
                    `app.modals.${inactive 
                      ? 'inactiveMultipleConfirmation'
                      : 'deleteMultipleConfirmation'
                    }.title`,
                    { title },
                  )}
            </Typography>
          </Box>
        </Box>
      }
      footer={
        <>
          {!hideCancel && (
            <Button
              variant="secondary"
              csx={[btnBaseStyles, btnCommonCsx, btnCancelCsx]}
              onClick={handleOnCancel}>
              {btnCancelText ? btnCancelText : t('commonButtons.cancel')}
            </Button>
          )}
          {!hideSuccess && (
            <Button
              variant="danger"
              csx={[btnBaseStyles, btnCommonCsx, btnSuccessCsx]}
              onClick={handleOnModalSuccess}>
              {btnSuccessText
                ? btnSuccessText
                : t(
                    `commonButtons.${inactive ? 'inactiveMultiple' : 'deleteMultiple'}`,
                    { description: `${ids.length} ${title}`}
                  )
              }
            </Button>
          )}
        </>
      }
      onModalClose={handleOnModalClose}>
      <Box>
        <Typography fontWeight='bold' csx={{ marginBottom: '1rem' }}>
          {description
            ? description
            : t(
                `app.modals.${inactive
                  ? 'inactiveMultipleConfirmation'
                  : 'deleteMultipleConfirmation'
                }.description`,
                { title },
              )}
        </Typography >
        {inactive && (
          <Typography csx={{ marginBottom: '1rem' }}>
            {t('app.modals.inactiveMultipleConfirmation.content')}
          </Typography >
        )}
        <Box csx={{ display: 'flex', marginBottom: '8px' }}>
          <Typography
            fontWeight='bold'
            variant='subtitle'
            color='semanticBlue'
            csx={{
              whiteSpace: 'nowrap',
              marginRight: '0.5rem',
            }}
            >
            {title} ({ids.length})
          </Typography>
          <Divider csx={{ width: '100%' }} />
        </Box>
        <List>
              {data && ids.length > 0
                ? ids.map(id => (
                  <ListItem
                    styleOverwrite={deleteListItemStyles}
                    key={`delete-item-${id}`}
                    text={(data as Record<string, any>)[id].title}
                    showLeftIcon
                    icon={<Icon name="FaCircle" size='10px' />}
                  />
                ))
                : null
              }
        </List>
      </Box>
    </Modal>
  );
};

export default DeleteMultipleConfirmationModal;
